import React, { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend, Cell } from 'recharts';
import { BarChart3, Calendar, Users, MessageCircle, Trophy, Percent, Info } from 'lucide-react';
import api from '../utils/api';
import RangoFechas, { rangoInicial } from '../components/RangoFechas';
import { STATUS_CONFIG, STATUS_ORDER } from '../utils/constants';

const tooltipStyle = { background: 'var(--tooltip-bg)', border: '1px solid var(--border)', borderRadius: 8, fontFamily: 'Raleway', color: 'var(--text-primary)' };
const tickStyle = { fill: 'var(--chart-text)', fontSize: 11 };
const channelColors = ['#2E7DE2', '#22c55e', '#f97316', '#a855f7', '#eab308', '#ef4444', '#06b6d4', '#ec4899'];

const PRESETS = [
  { key: '7', label: 'Última semana', days: 7 },
  { key: '30', label: 'Último mes', days: 30 },
  { key: '90', label: 'Último trimestre', days: 90 },
  { key: '365', label: 'Último año', days: 365 },
  { key: 'custom', label: 'Personalizado' },
];

const SOURCE_TYPE_LABELS = { web_form: 'Formulario web', research: 'Research', csv: 'Importación CSV', manual: 'Carga manual', whatsapp: 'WhatsApp', instagram: 'Instagram', apify: 'Research' };
const PLATFORM_LABELS = { google: 'Google Ads', facebook: 'Meta Ads', instagram: 'Meta Ads', meta: 'Meta Ads' };
const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const MONTHS_LONG = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const WEEKDAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

// "2026-09-02" -> partes sin problemas de zona horaria
const parts = (s) => { const [y, m, d] = String(s).slice(0, 10).split('-').map(Number); return { y, m, d, dow: new Date(Date.UTC(y, m - 1, d)).getUTCDay() }; };
const pad = (n) => String(n).padStart(2, '0');
const fmtDate = (s) => { const p = parts(s); return `${pad(p.d)}/${pad(p.m)}/${p.y}`; };
const axisLabel = (s, g) => { const p = parts(s); return g === 'month' ? `${MONTHS[p.m - 1]} ${String(p.y).slice(2)}` : `${pad(p.d)} ${MONTHS[p.m - 1]}`; };
const tooltipLabel = (s, g) => {
  const p = parts(s);
  if (g === 'month') return `${MONTHS_LONG[p.m - 1]} ${p.y}`;
  if (g === 'week') return `Semana del ${pad(p.d)}/${pad(p.m)}/${p.y}`;
  return `${WEEKDAYS[p.dow]} ${pad(p.d)}/${pad(p.m)}/${p.y}`;
};
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const daysAgoStr = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const GRANULARITY_TEXT = { day: 'por día', week: 'por semana', month: 'por mes' };
const fmtDuration = (h) => h >= 48 ? `${Math.round((h / 24) * 10) / 10} días` : `${Math.round(h)} h`;

const h3 = { fontSize: 16, fontWeight: 700, marginBottom: 4, color: 'var(--text-primary)' };
const sub = { fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 };
const empty = <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Sin datos en este período</p>;

function Kpi({ icon: Icon, label, value, help, color = 'var(--accent)' }) {
  return (
    <div className="card card-dato" style={{ padding: '14px 16px' }} title={help}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}><Icon size={14} color={color} /> {label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4 }}>{value}</div>
      {help && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{help}</div>}
    </div>
  );
}

function ConversionBar({ value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 60, height: 6, background: 'var(--bg-secondary)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(100, value || 0)}%`, height: '100%', background: '#22c55e', borderRadius: 3 }} />
      </div>
      <span style={{ fontWeight: 600, color: '#22c55e' }}>{value || 0}%</span>
    </div>
  );
}

export default function Reports() {
  // mismo selector y mismo estado que el super admin: { preset, desde, hasta }
  const [rango, setRango] = useState(() => rangoInicial('actual'));
  const [datasets, setDatasets] = useState([]);
  const [selectedDs, setSelectedDs] = useState('');
  const [funnel, setFunnel] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [conversion, setConversion] = useState([]);
  const [sourceData, setSourceData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get('/datasets').then(res => setDatasets(res.data)).catch(() => {}); }, []);

  const params = useMemo(() => {
    const p = { start_date: rango.desde, end_date: rango.hasta || todayStr() };
    if (selectedDs) p.dataset_id = selectedDs;
    return p;
  }, [rango, selectedDs]);

  useEffect(() => {
    if (!rango.desde) return;
    setLoading(true);
    Promise.all([
      api.get('/reports/activity-timeline', { params }).then(res => setTimeline(res.data)),
      api.get('/reports/funnel', { params }).then(res => setFunnel(res.data)),
      api.get('/reports/conversion-by-dataset', { params }).then(res => setConversion(res.data)),
      api.get('/reports/by-source', { params }).then(res => setSourceData(res.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const g = timeline?.range?.granularity || 'day';
  const series = timeline?.series || [];
  const hasActivity = series.some(r => r.leads || r.contacts || r.won);
  const totals = timeline?.totals;

  const funnelData = (funnel?.funnel || []).slice().sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status))
    .map(s => ({ name: STATUS_CONFIG[s.status]?.label || s.status, value: Number(s.count), fill: STATUS_CONFIG[s.status]?.color || '#64748b' }));
  const avgTimesData = (funnel?.avgTimes || []).filter(t => Number(t.avg_hours) > 0)
    .sort((a, b) => STATUS_ORDER.indexOf(a.new_status) - STATUS_ORDER.indexOf(b.new_status))
    .map(t => ({ name: STATUS_CONFIG[t.new_status]?.label || t.new_status, dias: Math.round((Number(t.avg_hours) / 24) * 10) / 10, horas: Number(t.avg_hours) }));

  const rangeText = timeline?.range ? `Del ${fmtDate(timeline.range.start)} al ${fmtDate(timeline.range.end)}` : '';
  const btn = (active) => ({ border: '1px solid ' + (active ? 'var(--accent)' : 'var(--border)'), background: active ? 'var(--accent)' : 'transparent', color: active ? '#fff' : 'var(--text-secondary)' });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <BarChart3 size={24} color="var(--accent)" />
        <h1 style={{ fontSize: 26, fontWeight: 800 }}>Reportes</h1>
      </div>

      {/* El mismo selector de período que usa el super admin */}
      <RangoFechas valor={rango} onChange={setRango}
        derecha={
          <select value={selectedDs} onChange={e => setSelectedDs(e.target.value)} style={{ width: 'auto', minWidth: 150, fontSize: 13 }}>
            <option value="">Todas las bases</option>
            {datasets.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        } />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)', margin: '-6px 2px 16px' }}>
        <Info size={12} /> Los reportes muestran los leads que ingresaron en este período{loading ? ' · actualizando…' : ''}
      </div>

      {/* Resumen */}
      {totals && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 16 }}>
          <Kpi icon={Users} label="Leads nuevos" value={totals.leads.toLocaleString('es-AR')} help="Ingresaron en el período" />
          <Kpi icon={MessageCircle} label="Contactos realizados" value={totals.contacts.toLocaleString('es-AR')} help="Mensajes enviados desde el CRM" color="#22c55e" />
          <Kpi icon={Trophy} label="Ganados" value={totals.won.toLocaleString('es-AR')} help="Pasaron a Ganado en el período" color="#eab308" />
          <Kpi icon={Percent} label="Conversión" value={totals.conversion_rate + '%'} help="Leads nuevos que ya están ganados" color="#a855f7" />
        </div>
      )}

      {/* Actividad en el tiempo (primer bloque) */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={h3}>Actividad en el tiempo</h3>
        <div style={sub}>Leads nuevos, contactos y ventas ganadas {GRANULARITY_TEXT[g]}</div>
        {hasActivity ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={series} margin={{ left: -10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="period" tick={tickStyle} tickFormatter={(v) => axisLabel(v, g)} minTickGap={16} />
              <YAxis tick={tickStyle} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} labelFormatter={(v) => tooltipLabel(v, g)} />
              <Legend wrapperStyle={{ color: 'var(--chart-text)', fontSize: 12 }} />
              <Line type="monotone" dataKey="leads" name="Leads nuevos" stroke="#2E7DE2" strokeWidth={2} dot={series.length <= 31 ? { r: 3 } : false} />
              <Line type="monotone" dataKey="web" name="Leads del formulario web" stroke="#f97316" strokeWidth={2} dot={series.length <= 31 ? { r: 3 } : false} />
              <Line type="monotone" dataKey="contacts" name="Contactos" stroke="#22c55e" strokeWidth={2} dot={series.length <= 31 ? { r: 3 } : false} />
              <Line type="monotone" dataKey="won" name="Ganados" stroke="#eab308" strokeWidth={2} dot={series.length <= 31 ? { r: 3 } : false} />
            </LineChart>
          </ResponsiveContainer>
        ) : empty}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, marginBottom: 20 }}>
        <div className="card">
          <h3 style={h3}>Estado de los leads</h3>
          <div style={sub}>En qué etapa están los leads que ingresaron en el período</div>
          {funnelData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={funnelData}>
                <XAxis dataKey="name" tick={tickStyle} angle={-30} textAnchor="end" height={70} interval={0} />
                <YAxis tick={tickStyle} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v, 'Leads']} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>{funnelData.map((e, i) => <Cell key={i} fill={e.fill} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : empty}
        </div>

        <div className="card">
          <h3 style={h3}>Tiempo promedio hasta cada estado</h3>
          <div style={sub}>Cuánto tarda un lead en pasar de su estado anterior a cada etapa (en días)</div>
          {avgTimesData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={avgTimesData} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" tick={tickStyle} />
                <YAxis dataKey="name" type="category" width={140} tick={tickStyle} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v, n, item) => [fmtDuration(item.payload.horas), 'Tiempo promedio']} />
                <Bar dataKey="dias" fill="var(--accent)" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : empty}
        </div>
      </div>

      {sourceData && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, marginBottom: 20 }}>
          <div className="card">
            <h3 style={h3}>Leads por canal</h3>
            <div style={sub}>De dónde vinieron los leads del período</div>
            {sourceData.bySource?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={sourceData.bySource}>
                  <XAxis dataKey="channel" tick={tickStyle} angle={-20} textAnchor="end" height={70} interval={0} />
                  <YAxis tick={tickStyle} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v, 'Leads']} />
                  <Bar dataKey="total" radius={[6, 6, 0, 0]}>{sourceData.bySource.map((e, i) => <Cell key={i} fill={channelColors[i % channelColors.length]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : empty}
          </div>

          <div className="card">
            <h3 style={h3}>Conversión por canal</h3>
            <div style={sub}>Qué canales traen leads que terminan ganados</div>
            {sourceData.bySource?.length > 0 ? (
              <div className="table-container">
                <table>
                  <thead><tr><th>Canal</th><th>Leads</th><th>Ganados</th><th>Conversión</th></tr></thead>
                  <tbody>
                    {sourceData.bySource.map((s, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{s.channel}</td>
                        <td style={{ color: 'var(--text-primary)' }}>{s.total}</td>
                        <td style={{ color: '#22c55e', fontWeight: 600 }}>{s.ganados}</td>
                        <td><ConversionBar value={s.conversion_rate} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : empty}
          </div>
        </div>
      )}

      {sourceData?.byCampaign?.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={h3}>Rendimiento por campaña</h3>
          <div style={sub}>Leads que llegaron desde cada campaña publicitaria</div>
          <div className="table-container">
            <table>
              <thead><tr><th>Campaña</th><th>Plataforma</th><th>Leads</th><th>Ganados</th><th>Conversión</th></tr></thead>
              <tbody>
                {sourceData.byCampaign.map((c, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{c.campaign}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{PLATFORM_LABELS[String(c.utm_source || '').toLowerCase()] || c.utm_source || '—'}</td>
                    <td style={{ color: 'var(--text-primary)' }}>{c.total}</td>
                    <td style={{ color: '#22c55e', fontWeight: 600 }}>{c.ganados}</td>
                    <td><ConversionBar value={c.conversion_rate} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sourceData?.byService?.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={h3}>Leads por servicio de interés</h3>
          <div style={sub}>Qué servicios consultan más (según el formulario web y el análisis IA de las conversaciones)</div>
          <div className="table-container">
            <table>
              <thead><tr><th>Servicio</th><th>Leads</th><th>Ganados</th><th>Conversión</th></tr></thead>
              <tbody>
                {sourceData.byService.map((s, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{s.service}</td>
                    <td style={{ color: 'var(--text-primary)' }}>{s.total}</td>
                    <td style={{ color: '#22c55e', fontWeight: 600 }}>{s.ganados}</td>
                    <td><ConversionBar value={s.conversion_rate} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card">
        <h3 style={h3}>Conversión por base de datos</h3>
        <div style={sub}>Resultados de cada base con los leads que ingresaron en el período</div>
        {conversion.length > 0 ? (
          <div className="table-container">
            <table>
              <thead><tr><th>Base de datos</th><th>Tipo</th><th>Leads</th><th>En proceso</th><th>Ganados</th><th>Perdidos</th><th>Conversión</th></tr></thead>
              <tbody>
                {conversion.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{c.name}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{SOURCE_TYPE_LABELS[c.source_type] || c.source_type}</td>
                    <td style={{ color: 'var(--text-primary)' }}>{c.total}</td>
                    <td style={{ color: 'var(--text-primary)' }}>{c.activos}</td>
                    <td style={{ color: '#22c55e', fontWeight: 600 }}>{c.ganados}</td>
                    <td style={{ color: '#ef4444' }}>{c.perdidos}</td>
                    <td><ConversionBar value={c.conversion_rate} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : empty}
      </div>
    </div>
  );
}
